import { useState } from 'react';

import type { VendorListItem } from '@keboola/api-client/developerPortal/types';
import { Button, Modal, setSubmitError, toast, useAppForm } from '@keboola/design';
import { Pencil } from '@keboola/design/icons';

import { SaveButton } from '../../../components/SaveButton';
import { selectSaveButtonState } from '../../../components/saveButtonState';
import { getApiErrorMessage } from '../../auth/errors';
import { formValidators } from '../../auth/validate';
import { useUpdateVendor } from '../queries';
import { editVendorFormSchema } from '../schemas';

const FORM_ID = 'edit-vendor';

/** The stored vendor as the form's fields hold it — a text input cannot carry `null`. */
const formValues = (vendor: VendorListItem) => ({
  name: vendor.name,
  email: vendor.email ?? '',
  address: vendor.address ?? '',
  projectUrl: vendor.projectUrl ?? '',
});

/**
 * Edits a vendor's own details from the profile page.
 *
 * The `id` is not editable here. apps-api assigns it, changing it would orphan every component published
 * under the old one, and the original only ever exposes the field to an admin creating a vendor from
 * scratch.
 */
export function EditVendorModal({ vendor }: { vendor: VendorListItem }) {
  const [open, setOpen] = useState(false);
  const updateVendor = useUpdateVendor();

  const form = useAppForm({
    defaultValues: formValues(vendor),
    validators: formValidators(editVendorFormSchema),
    canSubmitWhenInvalid: true,
    onSubmit: async ({ value, formApi }) => {
      const input = { ...value, email: value.email.trim() };
      try {
        await updateVendor.mutateAsync({ vendorId: vendor.id, input });
        toast.success('The vendor details have been updated.');
        // Reset *onto* what was just stored. `form.reset()` with no argument restores the values
        // captured when the form was created, which after a save are the pre-save ones — reopening
        // and editing a single field would then write the stale contents of every other field back.
        form.reset(input);
        setOpen(false);
      } catch (error) {
        setSubmitError(formApi, getApiErrorMessage(error, 'Could not save the vendor details.'));
      }
    },
  });

  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        // Reset on close so re-opening shows what is stored rather than a half-finished edit. The
        // current `vendor` is passed explicitly: a bare `form.reset()` would restore the first-render
        // defaults and undo whatever the last refetch brought in.
        if (!next) form.reset(formValues(vendor));
      }}
    >
      <Modal.Trigger asChild>
        <Button variant="invisible" size="icon-sm" aria-label={`Edit ${vendor.name}`}>
          <Pencil />
        </Button>
      </Modal.Trigger>
      <Modal.Content size="medium">
        <Modal.Header
          description={`Vendor ID ${vendor.id} — assigned by Keboola and not editable.`}
        >
          Edit vendor
        </Modal.Header>
        <Modal.Body>
          <form.AppForm>
            <form.FormBody id={FORM_ID}>
              <form.AppField name="name">
                {(field) => <field.TextField label="Name" required />}
              </form.AppField>
              <form.AppField name="address">
                {/* No `rows`: `TextareaField` does not forward it, so the height is whatever the design
                    system picks. Flagged upstream rather than worked around with a className. */}
                {(field) => <field.TextareaField label="Address" required />}
              </form.AppField>
              <form.AppField name="email">
                {(field) => (
                  <field.TextField
                    label="Email"
                    type="email"
                    required
                    help="Where Keboola contacts you about your components."
                  />
                )}
              </form.AppField>
              <form.AppField name="projectUrl">
                {(field) => (
                  <field.TextField
                    label="Test project URL"
                    type="url"
                    placeholder="https://connection.keboola.com/admin/projects/1234"
                    help="A Keboola project where your components can be tried out. Leave empty to remove it."
                  />
                )}
              </form.AppField>
              <form.SubmitErrorAlert />
            </form.FormBody>
          </form.AppForm>
        </Modal.Body>
        <Modal.Footer>
          <Modal.Close asChild>
            <Button type="button" variant="secondary">
              Cancel
            </Button>
          </Modal.Close>
          <form.Subscribe selector={selectSaveButtonState}>
            {(state) => (
              <SaveButton state={state} form={FORM_ID}>
                Save
              </SaveButton>
            )}
          </form.Subscribe>
        </Modal.Footer>
      </Modal.Content>
    </Modal>
  );
}
